import React, { useState, useEffect, useCallback } from 'react';
import { 
  Gear, 
  CaretDown, 
  CaretRight, 
  EnvelopeSimple, 
  Cloud, 
  Bell, 
  Key,
  Link,
  Eye,
  EyeSlash,
  Copy,
  Check,
  PencilSimple,
  FloppyDisk,
  X,
  ArrowsClockwise,
  ChatCircle,
  Code,
  ToggleLeft,
  ToggleRight,
  Warning,
  Buildings
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getDevSettings, updateDevSettings, queryCollection, batchUpdateUsersOrganizationId, batchUpdateUsersApartmentNumber, batchUpdateUsersAddress, getCommHubUsers } from '../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface IntegrationField {
  key: string;
  label: string;
  value?: string;
  secret?: boolean;
}

// Component to fix organization_id for all users
function FixOrganizationSection() {
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState<{ updated: number; failed: number } | null>(null);
  const [organizations, setOrganizations] = useState<Array<{ id: string; name?: string }>>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [userStats, setUserStats] = useState<Record<string, number>>({});
  const [loadingStats, setLoadingStats] = useState(false);

  const loadOrganizations = useCallback(async () => {
    try {
      const orgs = await queryCollection<{ id: string; name?: string }>('organizations');
      setOrganizations(orgs);
      if (orgs.length > 0) {
        setSelectedOrg(orgs[0].id);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  }, []);

  const loadUserStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const users = await getCommHubUsers({ limit: 500 });
      
      // Count users per organization_id
      const stats: Record<string, number> = {};
      for (const user of users) {
        const orgId = user.organization_id || 'no_org';
        stats[orgId] = (stats[orgId] || 0) + 1;
      }
      setUserStats(stats);
    } catch (error) {
      console.error('Failed to load user stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
    loadUserStats();
  }, [loadOrganizations, loadUserStats]);

  const handleFix = async () => {
    if (!selectedOrg) {
      toast.error('Välj en förening först');
      return;
    }

    if (!window.confirm('Är du säker? Detta kommer uppdatera organization_id för ALLA användare till den valda föreningen.')) {
      return;
    }

    setFixing(true);
    try {
      const res = await batchUpdateUsersOrganizationId(selectedOrg);
      setResult(res);
      toast.success(`Klart! ${res.updated} användare uppdaterade`);
      // Reload stats after update
      await loadUserStats();
    } catch (error) {
      console.error('Fix failed:', error);
      toast.error('Kunde inte uppdatera användare');
    } finally {
      setFixing(false);
    }
  };

  // Get organization name by ID
  const getOrgName = (orgId: string): string => {
    if (orgId === 'no_org') return 'Ingen förening';
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId.substring(0, 8) + '...';
  };

  return (
    <Card data-testid="fix-organization-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Buildings size={18} className="text-emerald-600" />
          Koppla användare till förening
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Uppdatera alla användare så att de är kopplade till rätt förening. 
          Detta behövs om användare visar "Okänd" under Förening eller om användare försvinner för admins.
        </p>

        {/* Diagnostik - visa hur användare är fördelade */}
        {loadingStats ? (
          <div className="p-3 bg-slate-50 rounded-lg mb-4 text-sm text-slate-500">
            Laddar användarstatistik...
          </div>
        ) : Object.keys(userStats).length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Användarfördelning per organisation:</p>
            <ul className="text-sm text-amber-700 space-y-1">
              {Object.entries(userStats).map(([orgId, count]) => (
                <li key={orgId} className="flex justify-between">
                  <span>{getOrgName(orgId)}</span>
                  <span className="font-mono">{count} användare</span>
                </li>
              ))}
            </ul>
            {Object.keys(userStats).length > 1 && (
              <p className="text-xs text-amber-600 mt-2 pt-2 border-t border-amber-200">
                💡 Om användare har olika föreningar kommer admins bara se sin egen förenings användare.
                Använd knappen nedan för att flytta ALLA till samma förening.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-4 mb-4">
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-64" data-testid="org-select">
              <SelectValue placeholder="Välj förening" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.name || org.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={handleFix} disabled={fixing || !selectedOrg} data-testid="update-org-btn">
            {fixing ? 'Uppdaterar...' : 'Flytta ALLA till vald förening'}
          </Button>
        </div>

        {result && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              <strong>{result.updated}</strong> användare uppdaterade
              {result.failed > 0 && <span className="text-red-600 ml-2">({result.failed} misslyckades)</span>}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Component to fix apartment_id (convert from UUID to apartment number)
function FixApartmentSection() {
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState<{ updated: number; failed: number; skipped: number } | null>(null);

  const handleFix = async () => {
    if (!window.confirm('Är du säker? Detta kommer konvertera alla apartment_id från interna ID:n till lägenhetsnummer.')) {
      return;
    }

    setFixing(true);
    try {
      const res = await batchUpdateUsersApartmentNumber();
      setResult(res);
      toast.success(`Klart! ${res.updated} användare uppdaterade`);
    } catch (error) {
      console.error('Fix failed:', error);
      toast.error('Kunde inte uppdatera lägenhetsnummer');
    } finally {
      setFixing(false);
    }
  };

  return (
    <Card data-testid="fix-apartment-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Buildings size={18} className="text-blue-600" />
          Konvertera lägenhetsnummer
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Konvertera apartment_id från interna UUID:er till lägenhetsnummer (t.ex. "5A", "1B").
          Detta fixar visningen i CommHub-databasen.
        </p>

        <Button onClick={handleFix} disabled={fixing} data-testid="update-apt-btn">
          {fixing ? 'Konverterar...' : 'Konvertera alla lägenhetsnummer'}
        </Button>

        {result && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-4">
            <p className="text-sm text-blue-800">
              <strong>{result.updated}</strong> användare uppdaterade
              {result.skipped > 0 && <span className="text-slate-600 ml-2">({result.skipped} överhoppade)</span>}
              {result.failed > 0 && <span className="text-red-600 ml-2">({result.failed} misslyckades)</span>}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Component to add address to users based on apartment -> property -> address
function FixAddressSection() {
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState<{ updated: number; failed: number; skipped: number } | null>(null);

  const handleFix = async () => {
    if (!window.confirm('Är du säker? Detta kommer lägga till adress för alla användare baserat på deras lägenhet och fastighet.')) {
      return;
    }

    setFixing(true);
    try {
      const res = await batchUpdateUsersAddress();
      setResult(res);
      toast.success(`Klart! ${res.updated} användare fick adress`);
    } catch (error) {
      console.error('Fix failed:', error);
      toast.error('Kunde inte uppdatera adresser');
    } finally {
      setFixing(false);
    }
  };

  return (
    <Card data-testid="fix-address-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Buildings size={18} className="text-purple-600" />
          Lägg till adress för användare
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Matchar användare → lägenhet → fastighet → adress och uppdaterar CommHub med rätt adress för varje användare.
        </p>

        <Button onClick={handleFix} disabled={fixing} data-testid="update-address-btn">
          {fixing ? 'Uppdaterar adresser...' : 'Uppdatera alla adresser'}
        </Button>

        {result && (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mt-4">
            <p className="text-sm text-purple-800">
              <strong>{result.updated}</strong> användare fick adress
              {result.skipped > 0 && <span className="text-slate-600 ml-2">({result.skipped} överhoppade)</span>}
              {result.failed > 0 && <span className="text-red-600 ml-2">({result.failed} misslyckades)</span>}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface Integration {
  id: string;
  name: string;
  type?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  status: string;
  description: string;
  enabled: boolean;
  fields: IntegrationField[];
  docs?: string;
}

export default function SuperAdminSettingsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // DEV Login state
  const [devLoginEnabled, setDevLoginEnabled] = useState(false);
  const [devLoginLoading, setDevLoginLoading] = useState(true);
  const [devLoginSaving, setDevLoginSaving] = useState(false);

  useEffect(() => {
    fetchIntegrations();
    fetchDevSettings();
  }, []);

  const fetchDevSettings = async () => {
    setDevLoginLoading(true);
    try {
      const settings = await getDevSettings();
      setDevLoginEnabled(settings.dev_login_enabled === true);
    } catch (error) {
      console.error('Failed to fetch dev settings:', error);
    } finally {
      setDevLoginLoading(false);
    }
  };

  const toggleDevLogin = async () => {
    setDevLoginSaving(true);
    const newValue = !devLoginEnabled;
    try {
      const result = await updateDevSettings({ dev_login_enabled: newValue });
      if (result.success) {
        setDevLoginEnabled(newValue);
        toast.success(newValue ? 'DEV-login aktiverat' : 'DEV-login avaktiverat');
      } else {
        toast.error('Kunde inte uppdatera inställning');
      }
    } catch (error) {
      console.error('Failed to toggle dev login:', error);
      toast.error('Ett fel uppstod');
    } finally {
      setDevLoginSaving(false);
    }
  };

  const fetchIntegrations = async () => {
    // Note: Integrations are managed via CommHub, not a backend API
    // This is a placeholder for future CommHub-based integration management
    // For now, we show an empty state
    setIntegrations([]);
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSecret = (fieldKey: string) => {
    setVisibleSecrets(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  const copyToClipboard = (value: string, fieldKey: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldKey);
    toast.success('Kopierat!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const startEditing = (fieldKey: string, currentValue: string | undefined) => {
    setEditingField(fieldKey);
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = async (_key: string) => {
    // Note: Integration settings are not yet implemented in serverless mode
    // This is a placeholder for future CommHub-based settings management
    setSaving(true);
    toast.info('Integrationsinställningar hanteras via CommHub');
    setSaving(false);
    setEditingField(null);
    setEditValue('');
  };

  const maskValue = (value: string | undefined) => {
    if (!value) return '-';
    if (value.length <= 8) return '••••••••';
    return value.substring(0, 4) + '••••••••' + value.substring(value.length - 4);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'communication': return <ChatCircle size={20} className="text-indigo-500" weight="duotone" />;
      case 'email': return <EnvelopeSimple size={20} className="text-blue-500" />;
      case 'storage': return <Cloud size={20} className="text-purple-500" />;
      case 'push': return <Bell size={20} className="text-green-500" />;
      case 'security': return <Key size={20} className="text-orange-500" />;
      default: return <Link size={20} className="text-slate-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'configured') {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Konfigurerad</span>;
    } else if (status === 'partial') {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Delvis</span>;
    }
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Ej konfigurerad</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gear size={28} weight="duotone" className="text-slate-700" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Systeminställningar</h1>
            <p className="text-sm text-slate-500">DEV-läge, externa kopplingar och API-nycklar</p>
          </div>
        </div>
      </div>

      {/* DEV Login Toggle Card */}
      <Card data-testid="dev-login-settings-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Code size={18} className="text-amber-600" />
            DEV-läge
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-100 flex items-center justify-center">
                <Code size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="font-medium text-slate-900">DEV-login på startsidan</div>
                <div className="text-sm text-slate-600">
                  Visa snabbknappar för att logga in som olika roller utan OTP
                </div>
              </div>
            </div>
            <button
              onClick={toggleDevLogin}
              disabled={devLoginLoading || devLoginSaving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                devLoginEnabled 
                  ? 'bg-amber-500 text-white hover:bg-amber-600' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              } ${(devLoginLoading || devLoginSaving) ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="dev-login-toggle"
            >
              {devLoginSaving ? (
                <span className="animate-pulse">Sparar...</span>
              ) : devLoginLoading ? (
                <span className="animate-pulse">Laddar...</span>
              ) : (
                <>
                  {devLoginEnabled ? (
                    <>
                      <ToggleRight size={24} weight="fill" />
                      <span>Aktivt</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft size={24} />
                      <span>Inaktivt</span>
                    </>
                  )}
                </>
              )}
            </button>
          </div>
          
          {devLoginEnabled && (
            <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <Warning size={18} weight="bold" />
              <span>
                <strong>Varning:</strong> DEV-login kringgår autentisering. Använd endast i testmiljöer!
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fix Organization Section */}
      <FixOrganizationSection />

      {/* Fix Apartment Section */}
      <FixApartmentSection />

      {/* Fix Address Section */}
      <FixAddressSection />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Externa Integrationer</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setIntegrations([]);
                fetchIntegrations();
              }} 
              className="gap-1"
            >
              <ArrowsClockwise size={14} />
              Uppdatera
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {integrations.map((integration) => (
              <div key={integration.id} className="bg-white">
                {/* Header row */}
                <button
                  onClick={() => toggleRow(integration.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {getIcon(integration.type || 'default')}
                    <div className="text-left">
                      <div className="font-medium text-slate-900">{integration.name}</div>
                      <div className="text-xs text-slate-500">{integration.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(integration.status)}
                    {expandedRows[integration.id] ? (
                      <CaretDown size={16} className="text-slate-400" />
                    ) : (
                      <CaretRight size={16} className="text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded content */}
                {expandedRows[integration.id] && (
                  <div className="px-4 pb-4 bg-slate-50 border-t">
                    <div className="pt-3 space-y-2">
                      {integration.fields.map((field) => {
                        const fieldKey = field.key;
                        const isSecret = field.secret;
                        const isVisible = visibleSecrets[fieldKey];
                        const isEditing = editingField === fieldKey;
                        const displayValue = isSecret && !isVisible ? maskValue(field.value) : (field.value || '-');
                        
                        return (
                          <div 
                            key={field.key} 
                            className="flex items-center justify-between py-2 px-3 bg-white rounded border text-sm"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 text-xs">{field.label}</span>
                              {isEditing ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <Input
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="h-8 font-mono text-sm"
                                    type={isSecret ? "password" : "text"}
                                    autoFocus
                                  />
                                  <Button 
                                    size="sm" 
                                    className="h-8 px-2"
                                    onClick={() => saveField(fieldKey)}
                                    disabled={saving}
                                  >
                                    <FloppyDisk size={14} />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="h-8 px-2"
                                    onClick={cancelEditing}
                                  >
                                    <X size={14} />
                                  </Button>
                                </div>
                              ) : (
                                <div className="font-mono text-slate-900 truncate">
                                  {displayValue}
                                </div>
                              )}
                            </div>
                            {!isEditing && (
                              <div className="flex items-center gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => startEditing(fieldKey, field.value)}
                                  title="Redigera"
                                >
                                  <PencilSimple size={14} />
                                </Button>
                                {isSecret && field.value && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => toggleSecret(fieldKey)}
                                    title={isVisible ? "Dölj" : "Visa"}
                                  >
                                    {isVisible ? <EyeSlash size={14} /> : <Eye size={14} />}
                                  </Button>
                                )}
                                {field.value && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => copyToClipboard(field.value || '', fieldKey)}
                                    title="Kopiera"
                                  >
                                    {copiedField === fieldKey ? (
                                      <Check size={14} className="text-green-500" />
                                    ) : (
                                      <Copy size={14} />
                                    )}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {integration.docs && (
                        <a 
                          href={integration.docs} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                        >
                          <Link size={12} />
                          Dokumentation
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {integrations.length === 0 && (
              <div className="px-4 py-8 text-center text-slate-500">
                Laddar integrationer...
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
